import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import BlogPage from './pages/BlogPage';
import ArticlePage from './pages/ArticlePage';
import PaymentPage from './pages/PaymentPage';
import ProcessingPage from './pages/ProcessingPage';
import SkillsSelectionPage from './pages/SkillsSelectionPage';
import ResultsPage from './pages/ResultsPage';
import SoftSkillsPage from './pages/SoftSkillsPage';
import SupportPage from './pages/SupportPage';
import TermsPage from './pages/TermsOfService';
import PrivacyPage from './pages/PrivacyPolicy';
import './styles/globals.css';
import './styles/BlogPage.css';
import { captureRefAndSource } from './hooks/attribution';
import csrfService from './services/csrfService';

function ScrollToHash({ offset = 0 }) {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash) return;

    const id = hash.slice(1); // remove '#'

    // Let the next paint happen so the element exists after route change
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;

      const y =
        el.getBoundingClientRect().top + window.pageYOffset - Number(offset || 0);

      window.scrollTo({ top: y, behavior: 'smooth' });
    });
  }, [hash, pathname, offset]);

  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return null;
}

// Layout component that wraps all routes with Navbar and Footer
function RootLayout() {
  const location = useLocation();

  useEffect(() => {
    captureRefAndSource({ firstTouch: true});
  }, [location.search]);

  useEffect(() => {
    // Initialize CSRF protection when app first loads
    csrfService.getToken().catch(err => {
      console.error('Failed to initialize CSRF protection:', err);
    });
  }, []);
  
  return (
    <>
      <ScrollToTop />
      <ScrollToHash offset={80} />
      <div className="app-container">
        <Navbar />
        <main className="main-content">
          <Outlet />
        </main>
        <Footer />
      </div>
    </>
  );
}

// Create the data router
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'blog', element: <BlogPage /> },
      { path: 'blog/:articleId', element: <ArticlePage /> },
      { path: 'payment', element: <PaymentPage /> },
      { path: 'processing', element: <ProcessingPage /> },
      { path: 'skills-selection', element: <SkillsSelectionPage /> },
      { path: 'soft-skills', element: <SoftSkillsPage /> },
      { path: 'results', element: <ResultsPage /> },
      { path: 'support', element: <SupportPage /> },
      { path: 'terms', element: <TermsPage /> },
      { path: 'privacy-policy', element: <PrivacyPage /> },
    ],
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
